import { cn } from '@/lib/utils';
import { Link } from '@inertiajs/react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { AuthorByline } from './AuthorByline';
import type { SectionTheme } from './constants';
import { articleUrl, itemVariants, sectionColors, sectionVariants } from './constants';
import type { JournalGuide, JournalSection } from './types';

function CompactCard({ guide, colors }: { guide: JournalGuide; colors: ReturnType<typeof sectionColors> }) {
    const tag = guide.kicker ?? guide.country_region ?? guide.tags?.[0]?.name;

    return (
        <Link href={articleUrl(guide.slug)} className="group">
            {guide.main_image && (
                <div className="relative aspect-[3/2] overflow-hidden">
                    <img
                        src={guide.main_image}
                        alt={guide.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                    />
                </div>
            )}
            <div className="pt-4">
                {tag && <span className={cn('lux-caps lux-caps-sm', colors.kicker)}>{tag}</span>}
                <h4 className={cn('lux-didot lux-didot-md mt-1 transition-colors', colors.cardTitle, colors.cardHover)}>{guide.title}</h4>
                {guide.excerpt && <p className={cn('lux-body lux-body-sm mt-2 line-clamp-2', colors.body)}>{guide.excerpt}</p>}
                {guide.author_name && <AuthorByline name={guide.author_name} className={cn('mt-3', colors.byline)} />}
            </div>
        </Link>
    );
}

interface SectionCompactProps {
    section: JournalSection;
    theme?: SectionTheme;
}

export default function SectionCompact({ section, theme = 'teal' }: SectionCompactProps) {
    const shouldReduceMotion = useReducedMotion();
    const colors = sectionColors(theme);
    const metaKicker = section.kicker;

    if (section.guides.length === 0) return null;

    const guides = section.guides.slice(0, 4);
    const hasMore = section.totalCount > 4;

    return (
        <motion.section
            id={section.tag}
            className={cn('lux-space-px scroll-mt-28 md:scroll-mt-32', colors.bg)}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={shouldReduceMotion ? undefined : sectionVariants}
        >
            <div className="mx-auto max-w-[1400px] py-14 lg:py-20">
                {/* Compact header */}
                <motion.div className="mb-8 lg:mb-10" variants={shouldReduceMotion ? undefined : itemVariants}>
                    {metaKicker && <span className={cn('lux-caps lux-caps-sm mb-1.5 block', colors.kicker)}>{metaKicker}</span>}
                    <div className="flex items-baseline justify-between gap-6">
                        <h2 className={cn('lux-didot lux-didot-xl', colors.heading)}>{section.name}</h2>
                        {hasMore && (
                            <Link
                                href={`/journal/category/${section.tag}`}
                                className={cn('lux-caps lux-caps-sm inline-flex shrink-0 items-center gap-1.5 transition-colors', colors.link)}
                            >
                                See all
                                <ArrowRight className="h-3.5 w-3.5" />
                            </Link>
                        )}
                    </div>
                    <div className="mt-4 flex items-center gap-0">
                        <div className={cn('h-0.5 w-10', colors.decorativeRule)} />
                        <div className={cn('h-px flex-1', colors.decorativeRule, 'opacity-50')} />
                    </div>
                </motion.div>

                {/* Single row of 4 vertical cards */}
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
                    {guides.map((guide) => (
                        <motion.div key={guide.id} variants={shouldReduceMotion ? undefined : itemVariants}>
                            <CompactCard guide={guide} colors={colors} />
                        </motion.div>
                    ))}
                </div>
            </div>
        </motion.section>
    );
}
